import React from "react";

import {CheckIcon} from "@heroicons/react/24/outline";
import NumberFlow, {NumberFlowGroup} from "@number-flow/react";

import * as auth from "@client/features/auth";
import * as dialogs from "@client/features/dialogs";

import * as graphics from "@client/app/graphics";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {appStyles} from "@client/style";
import * as utils from "@client/utils";

import {getRoundedCalculations, getRoundedQuantity} from "@client/calculations";
import {IngredientCard} from "@client/components/EntryCard";
import {EntryMetrics} from "@client/components/EntryMetrics";
import {ContentModal} from "@client/components/Modal";
import {NoOutstandingRequestsIndicator} from "@client/components/NoOutstandingRequestsIndicator";
import {Scene} from "@client/components/Scene";
import {Shell} from "@client/components/Shell";
import {useStage} from "@client/components/Stage";
import {UserMarkdown} from "@client/components/UserMarkdown";
import {RegistrationJourney} from "@client/journeys/registration";
import {Static} from "@client/shared/components/Static";
import {useSiteContext, useStaticURL} from "@client/site/context";
import * as forms from "@client/site/forms";
import {useAppDispatch} from "@client/store";
import {ShareAttribution, classNames, models, reverse, server} from "@reactivated";

const colors = [
    "oklch(51.1% 0.262 276.966)", // Protein (purple)
    "oklch(0.746 0.16 232.661)", // Carbs (blue)
    "oklch(0.718 0.202 349.761)", // Fat (pink)
];

type InlinePieChartProps = ReturnType<typeof getRoundedCalculations>;

function InlineLegend({summary}: InlinePieChartProps) {
    const {protein, carbs, fat} = summary;

    const macros = [protein, carbs, fat].some((macro) => Number.isNaN(macro.percentage))
        ? [{total: "N/A", percentage: 100, label: null}]
        : ([
              {...protein, label: "Protein"},
              {...carbs, label: "Carbs"},
              {...fat, label: "Fat"},
          ] as const);

    return (
        <div className="flex flex-1 flex-col justify-center gap-3">
            {macros.map((macro, index) => {
                if (macro.label === null) return null;

                return (
                    <div key={macro.label} className="flex items-center gap-3">
                        <div
                            className="size-8 rounded-full"
                            style={{backgroundColor: colors[index]}}
                        />
                        <div className="flex flex-col">
                            <div className="flex items-center text-base/none font-normal">
                                <span className="flex-1">{macro.label} </span>
                                <span
                                    className={`
                                        ml-2 inline-block rounded-lg bg-stone-200/60
                                        px-2 py-0.5 text-xs font-light text-gray-800
                                    `}
                                >
                                    <NumberFlowGroup>
                                        <span
                                            style={
                                                {
                                                    fontVariantNumeric: "tabular-nums",
                                                    "--number-flow-char-height":
                                                        "0.85em",
                                                } as React.CSSProperties
                                            }
                                        >
                                            <NumberFlow
                                                value={macro.percentage}
                                                format={{maximumFractionDigits: 0}}
                                                suffix="%"
                                            />
                                        </span>
                                    </NumberFlowGroup>
                                </span>
                            </div>
                            <div className="flex text-sm tracking-tighter">
                                <NumberFlowGroup>
                                    <span
                                        style={
                                            {
                                                fontVariantNumeric: "tabular-nums",
                                                "--number-flow-char-height": "0.85em",
                                            } as React.CSSProperties
                                        }
                                    >
                                        <NumberFlow
                                            value={parseFloat(macro.total ?? "0")}
                                            format={{maximumFractionDigits: 0}}
                                            suffix=" g"
                                        />
                                    </span>
                                </NumberFlowGroup>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export function InlinePieChart({summary, calories}: InlinePieChartProps) {
    const protein = summary.protein.percentage;
    const carbs = summary.carbs.percentage;

    const stops = [
        `${colors[0]} 0 ${protein}%`,
        `${colors[1]} ${protein}% ${protein + carbs}%`,
        `${colors[2]} ${protein + carbs}% 100%`,
    ].join(", ");

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "inline-block",
            }}
        >
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    background: `conic-gradient(${stops})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div
                    className="text-2xl"
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "75%",
                        height: "75%",
                        borderRadius: "50%",
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 200,
                        color: "#222",
                        textAlign: "center",
                    }}
                >
                    <NumberFlowGroup>
                        <span
                            className="tracking-tight"
                            style={
                                {
                                    fontVariantNumeric: "tabular-nums",
                                    "--number-flow-char-height": "0.85em",
                                } as React.CSSProperties
                            }
                        >
                            <NumberFlow
                                value={parseFloat(calories ?? "0")}
                                format={{maximumFractionDigits: 0}}
                                suffix=" cal"
                            />
                        </span>
                    </NumberFlowGroup>
                </div>
            </div>
        </div>
    );
}

export function ShareLayout(props: {
    share: {
        uuid: string;
        type: "prompt" | "recipe";
    } | null;
    title: string;
    fadedScroll?: "FULL" | "FADE_ONLY";
    cta?:
        | {title: string; onClick: () => unknown; content?: React.ReactNode}
        | null
        | "NONE"
        | "BLANK";
    children: React.ReactNode;
}) {
    const hasTrackedScroll = React.useRef(false);

    React.useEffect(() => {
        const scroller = document.getElementById("app-scroller");
        if (!scroller) {
            return;
        }

        const handleScroll = () => {
            if (scroller.scrollTop > 200 && !hasTrackedScroll.current) {
                void analytics.track("SCROLLED_SHARE");
                hasTrackedScroll.current = true;
                // Remove listener after tracking to avoid further checks
                scroller.removeEventListener("scroll", handleScroll);
            }
        };

        scroller.addEventListener("scroll", handleScroll);

        return () => {
            scroller.removeEventListener("scroll", handleScroll);
        };
    }, []); // Empty dependency array ensures this runs once on mount and cleans up on unmount

    React.useEffect(() => {
        // From https://deephive.de

        if (props.share == null) {
            return;
        }

        // const fallback = window.location.href;
        const app = `com.joyapp://redirect/share/${props.share.uuid}/`;

        if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            window.location.href = app;
            /*
            window.setTimeout(function() {
                // fallback is the same as the current page, so it's not needed
                window.location.href = fallback;
            }, 25);
            */
        }

        function killPopup() {
            window.removeEventListener("pagehide", killPopup);
        }
        window.addEventListener("pagehide", killPopup);
    }, []);

    const STATIC_URL = useStaticURL();
    React.useEffect(() => {
        if (props.share == null) {
            return;
        }
        const attribution: ShareAttribution = {
            attribution_type: "share",
            share_type: props.share.type,
            share_uuid: props.share.uuid,
            referrer: null,
        };
        analytics.register(attribution);
        void server.core.rpc.attribute_share(attribution);
        void analytics.track("SHARE_VIEW");
    }, []);
    const registrationDialog = dialogs.useDialog<server.core.rpc.Profile.output>({
        Content: RegistrationJourney,
        modal: ContentModal,
    });

    const {
        title: ctaTitle,
        onClick: ctaOnClick,
        content: ctaContent,
    } = props.cta == "NONE" || props.cta == "BLANK"
        ? {title: null, onClick: null}
        : props.cta != null
          ? props.cta
          : {
                title: "Sign Up",
                onClick: async () => {
                    const registrationResult = await registrationDialog();

                    if (registrationResult != null) {
                        void (await analytics.registered(
                            registrationResult.user.tracking_key,
                            "share",
                            "trial",
                        ));
                        window.location.href = reverse("journal");
                    }
                },
            };

    const bottomSection =
        props.cta == "BLANK" ? null : (
            <div className="px-3 py-1">
                {typeof props.cta != "string" && props.cta?.content != null ? (
                    props.cta.content
                ) : (
                    <div className="flex items-center gap-2">
                        <div className="flex flex-1 items-center gap-4">
                            <a className="" href={reverse("download")}>
                                <graphics.Logo className="size-12" />
                            </a>
                            {ctaContent != null ? (
                                ctaContent
                            ) : (
                                <div
                                    className="
                                        flex items-center space-x-2 text-pretty
                                        max-sm:-ml-2 max-sm:space-x-1 max-sm:text-xs
                                    "
                                >
                                    <span className="text-gray-700">Nutrition</span>
                                    <div className="size-1 rounded-full bg-indigo-600" />
                                    <span className="text-gray-700">Workouts</span>
                                    <div className="size-1 rounded-full bg-indigo-600" />
                                    <span className="text-gray-700">Habits</span>
                                </div>
                            )}
                        </div>
                        {ctaTitle != null && (
                            <button
                                onClick={ctaOnClick}
                                type="button"
                                className="
                                    rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm
                                    font-semibold whitespace-nowrap text-white shadow-xs
                                    hover:bg-indigo-500
                                    focus-visible:outline-2
                                    focus-visible:outline-offset-2
                                    focus-visible:outline-indigo-600
                                    focus-visible:outline-solid
                                "
                            >
                                {ctaTitle}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );

    const title = `${props.title} | Joy Health Tracker`;
    return (
        <Shell>
            <div className={appStyles.app}>
                <Static />
                <meta charSet="UTF-8" />
                <meta
                    name="viewport"
                    content="viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
                />
                <title>{title}</title>
                <meta property="og:title" content={title} />
                <link rel="icon" href={`${STATIC_URL}favicon.png`} type="image/png" />
                {registrationDialog.dialog}

                <NoOutstandingRequestsIndicator />
                <Scene
                    fadedScroll={props.fadedScroll}
                    actions={[]}
                    tabs={[]}
                    topSection={null}
                    bottomSection={bottomSection}
                    more={null}
                    locator={null}
                >
                    {props.children}
                </Scene>
            </div>
        </Shell>
    );
}

function PromptShare(props: {share: server.journal.templates.PromptShare.output}) {
    const profile = useSiteContext().profile;

    const stage = useStage({
        entries: props.share.prompt.entries,
        target: null,
    });

    return (
        <ShareLayout
            share={{uuid: props.share.share_uuid, type: "prompt"}}
            title={props.share.prompt.name}
        >
            <div className="">
                {props.share.prompt.image != null && (
                    <div className="flex-1 text-center">
                        <meta
                            property="og:image"
                            content={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.share.prompt.image.image_uid}/public`}
                        />
                        <div
                            className="
                                inline-block rounded-md border-2 border-sky-500 p-0.5
                            "
                        >
                            <img
                                className="
                                    max-h-[25vh] w-full rounded-md object-contain
                                "
                                src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.share.prompt.image.image_uid}/public`}
                                alt="Uploaded image"
                            />
                        </div>
                    </div>
                )}
                {profile?.has_hidden_macros !== true && (
                    <EntryMetrics size="condensed" calculations={stage.calculations} />
                )}
                <div className="mt-4">{stage.entries}</div>
            </div>
        </ShareLayout>
    );
}

function useRecipeRegistration(recipeUuid: string) {
    const [isSaving, setIsSaving] = React.useState(false);
    const dispatch = useAppDispatch();
    const registrationForm = auth.useRegistrationForm();

    const handleSubmit = React.useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            setIsSaving(true);

            try {
                const registrationResult = await server.core.rpc.register({
                    email: registrationForm.values.email,
                    with_notification: false,
                    invite_code: "share",
                    recipe_uuid: recipeUuid,
                    source: "share",
                    platform: "web",
                });

                if (registrationResult.type == "invalid") {
                    registrationForm.processErrors(registrationResult.errors);
                    setIsSaving(false);
                    void analytics.track("INVALID_REGISTRATION_FORM");
                    return;
                } else if (registrationResult.type != "success") {
                    setIsSaving(false);
                    return;
                }

                await dispatch(auth.postLogin(registrationResult.data, true));
                void (await analytics.registered(
                    registrationResult.data.user.tracking_key,
                    "share",
                    "trial",
                ));
                window.location.href = reverse("journal");
            } catch (error) {
                setIsSaving(false);
                throw error;
            }
        },
        [registrationForm, recipeUuid, dispatch],
    );

    return {
        registrationForm,
        isSaving,
        handleSubmit,
    };
}

function InlineRecipeRegistration(props: {
    recipeShare: models["journal.nutrition.RecipeShare"];
    recipe_button_experiment_name: models["journal.templates.RecipeButtonExperimentName"];
    recipe_price_experiment_name: models["journal.templates.RecipePriceExperimentName"];
}) {
    const {registrationForm, isSaving, handleSubmit} = useRecipeRegistration(
        props.recipeShare.recipe.uuid,
    );

    return (
        <div className="flex flex-col gap-2 px-0 py-2">
            <div className="flex justify-center gap-2 text-center">
                <div className="text-center">
                    <p className="text-base font-medium">Loved this recipe?</p>
                    <p className="text-sm font-light">Save it. And unlock many more.</p>
                </div>
            </div>
            <form
                className="flex gap-1"
                onSubmit={handleSubmit}
                onFocus={() => {
                    void analytics.track("FOCUSED_ON_REGISTRATION_FORM");
                }}
            >
                <label className="w-full flex-1">
                    <forms.PWidget
                        field={registrationForm.fields.email}
                        className="w-full rounded-md border border-gray-300 p-2 text-sm"
                    />
                </label>
                <div className="flex flex-col justify-center gap-2">
                    <typography.AsyncButton
                        forceLoading={isSaving}
                        type="submit"
                        className="h-full rounded-md bg-indigo-600 px-4 text-white"
                    >
                        {props.recipe_button_experiment_name == "FREE_TRIAL"
                            ? "Start Trial"
                            : "Save"}
                    </typography.AsyncButton>
                </div>
            </form>

            {registrationForm.errors?.email != null && (
                <div className="-mt-1.5 px-1 text-xs text-red-700">
                    {registrationForm.errors.email}
                </div>
            )}
            <div className="text-center text-xs font-light text-gray-700">
                {props.recipe_price_experiment_name == "FULL_TERMS" ? (
                    <>No credit card required. Free for 7 days.</>
                ) : props.recipe_price_experiment_name == "HIDDEN" ? (
                    <></>
                ) : (
                    <>Free trial. No credit card required.</>
                )}
            </div>
        </div>
    );
}

function RecipeRegistration(props: {
    recipeShare: models["journal.nutrition.RecipeShare"];
    recipe_button_experiment_name: models["journal.templates.RecipeButtonExperimentName"];
    recipe_price_experiment_name: models["journal.templates.RecipePriceExperimentName"];
}) {
    const {registrationForm, isSaving, handleSubmit} = useRecipeRegistration(
        props.recipeShare.recipe.uuid,
    );

    return (
        <div className="flex flex-col gap-4 px-4 py-6">
            <div className="flex justify-center">
                <graphics.DarkLogoWide className="h-auto w-48" />
            </div>

            <div
                className="
                    grid grid-cols-3 gap-2
                    *:overflow-hidden *:rounded-lg
                "
            >
                <img
                    src="https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/81279a0a-c8a2-4559-53ae-e39ffdb7eb00/thumbnail"
                    className="h-auto w-full"
                ></img>
                <img
                    src="https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/3a57d735-c00e-4c50-73b5-fc3ec17cf500/thumbnail"
                    className="h-auto w-full"
                ></img>
                <img
                    src="https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/83c0e726-ba5f-472b-c72e-a7c53d60cb00/thumbnail"
                    className="h-auto w-full"
                ></img>
            </div>
            <div className="text-center">
                <p className="text-lg font-medium">Loved this recipe?</p>
                <p className="text-sm font-light">Save it. And unlock many more.</p>
            </div>
            <ul className="mx-auto list-none space-y-2 text-sm font-light">
                <li className="flex items-center">
                    <CheckIcon
                        className="
                            mr-2 size-5 rounded-full bg-indigo-500 p-[0.18rem]
                            text-white
                        "
                    />
                    Customized goals, macros and habits
                </li>
                <li className="flex items-center">
                    <CheckIcon
                        className="
                            mr-2 size-5 rounded-full bg-indigo-500 p-[0.18rem]
                            text-white
                        "
                    />
                    20 minute workouts that deliver results
                </li>
                <li className="flex items-center">
                    <CheckIcon
                        className="
                            mr-2 size-5 rounded-full bg-indigo-500 p-[0.18rem]
                            text-white
                        "
                    />
                    <span>
                        Recipes that match <span className="font-medium">your</span>{" "}
                        macros
                    </span>
                </li>
            </ul>
            <form
                className="flex flex-col items-center gap-2"
                onSubmit={handleSubmit}
                onFocus={() => {
                    void analytics.track("FOCUSED_ON_REGISTRATION_FORM");
                }}
            >
                <label className="">
                    <forms.PWidget
                        field={registrationForm.fields.email}
                        className="w-64 rounded-md border border-gray-300 p-2 text-sm"
                    />
                    {registrationForm.errors?.email != null && (
                        <div className="mt-1 px-1 text-xs text-red-700">
                            {registrationForm.errors.email}
                        </div>
                    )}
                </label>
                <div className="flex flex-col justify-center gap-2">
                    <typography.AsyncButton
                        forceLoading={isSaving}
                        type="submit"
                        className="rounded-md bg-indigo-600 px-4 py-2 text-white"
                    >
                        {props.recipe_button_experiment_name == "FREE_TRIAL"
                            ? "Start My Free Trial"
                            : "Build My Personal Plan"}
                    </typography.AsyncButton>

                    {props.recipe_price_experiment_name == "FULL_TERMS" ? (
                        <>
                            <div className="text-center text-xs font-light text-gray-700">
                                No credit card required.
                            </div>

                            <div className="text-center text-xs font-light text-gray-700">
                                Free for 7 days. Then $5/month. Cancel anytime.
                            </div>
                        </>
                    ) : props.recipe_price_experiment_name == "HIDDEN" ? (
                        <></>
                    ) : (
                        <div className="text-center text-xs font-light text-gray-700">
                            Free trial. No credit card required.
                        </div>
                    )}
                </div>
            </form>
        </div>
    );
}

export function RecipeShare(props: {
    share: models["journal.nutrition.RecipeShare"];
    type:
        | "embed"
        | {
              recipe_experiment_name: models["journal.templates.RecipeExperimentName"];
              recipe_button_experiment_name: models["journal.templates.RecipeButtonExperimentName"];
              recipe_price_experiment_name: models["journal.templates.RecipePriceExperimentName"];
              instacart_experiment_name: models["journal.templates.InstacartExperimentName"];
          };
}) {
    const [perServing, setPerServing] = React.useState(true);
    const stage = useStage({
        entries: props.share.recipe.expanded_recipe,
        target: null,
        cardComponent: IngredientCard,
        divideByServings: perServing
            ? props.share.recipe.container_servings
            : undefined,
    });

    const {request} = useSiteContext();
    const profile = useSiteContext().profile;
    const subtitle = `${getRoundedQuantity(props.share.recipe.container_servings)} ${props.share.recipe.serving_size_unit}`;

    const shouldGenenerateOpenGraph =
        new URL(request.url).searchParams.get("_og") != null;

    const recipeName = props.share.recipe.name;

    if (shouldGenenerateOpenGraph) {
        return (
            <Shell>
                <div className="-mt-4 flex items-center px-20 pt-64">
                    <NoOutstandingRequestsIndicator />
                    <Static />
                    <div
                        className="
                            absolute inset-x-0 top-0 w-dvw space-y-2 py-4 text-center
                        "
                    >
                        <h1
                            className={classNames(
                                recipeName.length > 35 ? "text-5xl" : "text-6xl",
                                "font-light",
                            )}
                        >
                            {recipeName}
                        </h1>
                        <h2
                            className={classNames(
                                recipeName.length > 35 ? "text-4xl" : "text-5xl",
                                "font-extralight",
                            )}
                        >
                            {subtitle}
                        </h2>
                    </div>
                    <div className="flex-1">
                        <graphics.Logo className="h-[200px] w-[350px]" />
                    </div>
                    {/* thum.io needs more padding on the right for some reason. More than you think visually looking locally.  */}
                    <div className="pr-36">
                        <div className="flex scale-[180%] gap-6">
                            <div className="flex flex-1 flex-col justify-center">
                                <InlineLegend summary={stage.calculations.summary} />
                            </div>
                            <div className="size-40">
                                <InlinePieChart
                                    summary={stage.calculations.summary}
                                    calories={stage.calculations.calories}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Shell>
        );
    }

    const instacartCTA = (
        <div
            className="
                relative -mx-1 mt-2 items-center rounded-xl bg-blue-100 px-4 text-left
            "
        >
            <div className="flex flex-col gap-4 px-4 py-6">
                <div className="text-center">
                    <p className="text-lg/5 font-medium">Buy the ingredients</p>
                </div>
                <ul className="mx-auto list-none space-y-2 text-sm font-light">
                    <li className="flex items-center">
                        <CheckIcon
                            className="
                                mr-2 size-5 rounded-full bg-indigo-500 p-[0.18rem]
                                text-white
                            "
                        />
                        Exact quantities already set
                    </li>
                    <li className="flex items-center">
                        <CheckIcon
                            className="
                                mr-2 size-5 rounded-full bg-indigo-500 p-[0.18rem]
                                text-white
                            "
                        />
                        Swap or remove items you have
                    </li>
                    <li className="flex items-center">
                        <CheckIcon
                            className="
                                mr-2 size-5 rounded-full bg-indigo-500 p-[0.18rem]
                                text-white
                            "
                        />
                        Delivery or pickup — your choice
                    </li>
                </ul>
                <div className="flex flex-col items-center justify-center gap-2">
                    <typography.AsyncButton
                        className="flex items-center gap-2 rounded-md"
                        style={{
                            height: "46px",
                            paddingTop: "16px",
                            paddingBottom: "16px",
                            paddingLeft: "18px",
                            paddingRight: "18px",
                            backgroundColor: "#003D29",
                            color: "#FAF1E5",
                        }}
                        onClick={async () => {
                            void analytics.track("CLICKED_INSTACART_CTA");

                            const partner_linkback_url = `${utils.getBaseURLWithOriginForCapacitor()}/share/${
                                props.share.share_uuid
                            }/`;

                            const instructions = props.share.recipe.notes
                                // Convert escaped sequences into real newlines
                                .replace(/\\r\\n|\\n|\\r/g, "\n")
                                // Normalize multiple blank lines into a single newline
                                .replace(/\n+/g, "\n")
                                // Now split safely
                                .split("\n")
                                // Remove empty or whitespace-only entries
                                .map((s) => s.trim())
                                .filter(Boolean);

                            const result = await server.finance.rpc.instacart_recipe({
                                title: props.share.recipe.name,
                                image_url:
                                    props.share.recipe.image != null
                                        ? `https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.share.recipe.image.image_uid}/public`
                                        : null,
                                link_type: "recipe",
                                instructions,
                                ingredients: props.share.recipe.expanded_recipe.map(
                                    (e) => ({
                                        name: e.name,
                                        display_text: e.name,
                                        measurements: [
                                            {
                                                quantity: parseFloat(e.quantity),
                                                unit: e.unit,
                                            },
                                        ],
                                    }),
                                ),
                                landing_page_configuration: {
                                    partner_linkback_url,
                                    enable_pantry_items: true,
                                },
                            });

                            if (result.type != "success" || result.data == null) {
                                alert("Error adding recipe to Instacart");
                            } else {
                                /*
                                // TODO: Uh.. capacitor?
                                */
                                window.location.href = result.data;
                                // Force our async button to keep showing loading while we redirect.
                                return utils.delay(10000);
                            }
                        }}
                    >
                        <graphics.InstacartCarrot className="size-[22px]" />
                        Get Recipe Ingredients
                    </typography.AsyncButton>
                </div>
            </div>
        </div>
    );

    const inlineCTA = [].length == 1 && props.type != "embed" && (
        <div
            className="
                relative -mx-1 mt-2 items-center rounded-xl bg-blue-100 px-4 text-left
            "
        >
            <RecipeRegistration
                recipeShare={props.share}
                recipe_button_experiment_name={props.type.recipe_button_experiment_name}
                recipe_price_experiment_name={props.type.recipe_price_experiment_name}
            />
        </div>
    );

    const sections =
        props.share.recipe.sections.length == 0
            ? [
                  {
                      name: null,
                      entryUUIds: props.share.recipe.expanded_recipe.map((e) => e.uuid),
                  },
              ]
            : props.share.recipe.sections.map((section) => ({
                  name: section.name,
                  entryUUIds: section.ingredients.map((e) => e.uuid),
              }));

    const content = (
        <div>
            {props.share.recipe.preview?.image_uid != null && (
                <meta
                    property="og:image"
                    content={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.share.recipe.preview.image_uid}/public`}
                />
            )}
            {props.share.recipe.image != null && (
                <div className="-mx-1 mt-2 flex justify-center">
                    <img
                        className="
                            w-full max-w-(--breakpoint-sm) rounded-xl object-cover
                        "
                        src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.share.recipe.image.image_uid}/public`}
                        alt="Uploaded image"
                    />
                </div>
            )}
            <div
                className="
                    relative -mx-1 mt-2 items-center rounded-xl bg-white px-4 py-3
                    text-left
                "
            >
                <div className="mb-2">
                    <h1 className="mb-2 text-[1.65rem]/8 font-extralight">
                        {props.share.recipe.name}
                    </h1>
                    <h2 className="-mt-0.5 flex-1 text-xl font-extralight">
                        {subtitle}
                    </h2>
                </div>
                <div>
                    <div className="flex">
                        <div className="flex flex-1 flex-col justify-center">
                            <InlineLegend summary={stage.calculations.summary} />
                        </div>
                        <div className="size-40">
                            <InlinePieChart
                                summary={stage.calculations.summary}
                                calories={stage.calculations.calories}
                            />
                        </div>
                    </div>
                </div>
                <div className="mt-2.5 flex justify-center">
                    <div
                        className="
                            mx-auto inline-flex gap-2 text-sm font-light
                            *:rounded-md *:px-2 *:py-0.5
                        "
                    >
                        <button
                            className={classNames(
                                perServing ? "bg-red-100" : "bg-stone-100",
                            )}
                            onClick={() => setPerServing(true)}
                        >
                            Per serving
                        </button>
                        <button
                            className={classNames(
                                perServing ? "bg-stone-100" : "bg-red-100",
                            )}
                            onClick={() => setPerServing(false)}
                        >
                            Per recipe
                        </button>
                    </div>
                </div>
            </div>
            {[].length == 1 &&
                profile == null &&
                props.type != "embed" &&
                props.type.recipe_experiment_name == "CONTENT_START_REGISTRATION_V2" &&
                inlineCTA}
            {(typeof window == "undefined" || window.cordova == null) &&
                props.type != "embed" &&
                props.type.instacart_experiment_name == "INSTACART_CTA_TOP" &&
                instacartCTA}
            <div className="-mx-1 mt-2 items-center rounded-xl bg-white p-4 text-left">
                <h2 className="text-2xl font-extralight">Ingredients</h2>
                {sections.map((section, index) => (
                    <div key={index} className="">
                        {section.name != null && (
                            <h3 className="mt-3 -mb-2 text-lg font-normal">
                                {section.name}
                            </h3>
                        )}
                        <div
                            className="
                                mx-0.5 mt-0 divide-y divide-gray-300
                                *:py-2.5
                            "
                        >
                            {stage.bulkEntriesContent((e) =>
                                section.entryUUIds.includes(e.uuid),
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {(typeof window == "undefined" || window.cordova == null) &&
                (props.type == "embed" ||
                    props.type.instacart_experiment_name == "INSTACART_CTA_BOTTOM") &&
                instacartCTA}
            {[].length == 1 &&
                profile == null &&
                props.type != "embed" &&
                props.type.recipe_experiment_name == "CONTENT_MIDDLE_REGISTRATION_V2" &&
                inlineCTA}
            {props.share.recipe.notes != "" && (
                <div
                    className="
                        -mx-1 mt-2 items-center rounded-xl bg-white p-4 text-left
                    "
                >
                    <div className="space-y-4">
                        <div className="text-2xl font-extralight">Instructions</div>
                        <UserMarkdown content={props.share.recipe.notes} />
                    </div>
                </div>
            )}
        </div>
    );

    if (props.type == "embed") {
        return content;
    }

    const cta =
        profile != null
            ? {
                  title: "Copy Recipe",
                  onClick: async () => {
                      await server.journal.nutrition.copy_recipe({
                          recipe_id: props.share.recipe.id,
                      });
                      void analytics.track("COPIED_SHARED_RECIPE");
                      window.location.href = "/recipes/";
                  },
              }
            : [].length == 1 &&
                props.type.recipe_experiment_name == "CTA_INLINE_REGISTRATION_V2"
              ? {
                    title: "Save",
                    content: (
                        <InlineRecipeRegistration
                            recipeShare={props.share}
                            recipe_button_experiment_name={
                                props.type.recipe_button_experiment_name
                            }
                            recipe_price_experiment_name={
                                props.type.recipe_price_experiment_name
                            }
                        />
                    ),
                    onClick: () => {},
                }
              : "BLANK";

    return (
        <ShareLayout
            share={{uuid: props.share.share_uuid, type: "recipe"}}
            title={props.share.recipe.name}
            fadedScroll="FULL"
            cta={cta}
        >
            {content}
        </ShareLayout>
    );
}

export function Template(props: server.journal.templates.Share) {
    if ("recipe" in props.share) {
        const {share} = props;
        // useLayoutEffect ensures this runs before all other analytics in child components.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useLayoutEffect(() => {
            analytics.register({
                recipe_experiment_name: share.recipe_experiment_name,
                recipe_button_experiment_name: share.recipe_button_experiment_name,
                recipe_price_experiment_name: share.recipe_price_experiment_name,
                instacart_experiment_name: share.instacart_experiment_name,
            });
        }, []);
        analytics.useTrackPageView();
        return (
            <RecipeShare
                share={props.share.recipe}
                type={{
                    recipe_experiment_name: props.share.recipe_experiment_name,
                    recipe_button_experiment_name:
                        props.share.recipe_button_experiment_name,
                    recipe_price_experiment_name:
                        props.share.recipe_price_experiment_name,
                    instacart_experiment_name: props.share.instacart_experiment_name,
                }}
            />
        );
    }
    return <PromptShare share={props.share.prompt} />;
}
